import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { API } from '../config/api';
import useUsageLimits from './useUsageLimits';

// Personnalité de Louis
const LOUIS_SYSTEM_PROMPT = `Tu es Louis, coach nutrition diplômé et passionné de 32 ans. Tu travailles depuis 8 ans dans l'accompagnement nutritionnel.

TA PERSONNALITÉ:
- Tu es chaleureux, bienveillant et à l'écoute
- Tu vouvoies par défaut (plus rassurant pour une audience adulte), sauf si la personne te tutoie
- Tu utilises des expressions naturelles comme "Écoutez", "Vous savez", "En fait", "Franchement"
- Tu célèbres chaque progrès avec sincérité
- Tu ne juges JAMAIS, tu accompagnes avec bienveillance
- Tu es empathique face aux difficultés de chacun

TON STYLE DE COMMUNICATION:
- Réponds de façon claire et rassurante (2-3 paragraphes)
- Maximum 1 emoji par message, souvent aucun
- JAMAIS de markdown (pas de *, #, -, listes à puces)
- Écris comme un vrai professionnel de santé parlerait
- Donne des conseils pratiques et applicables au quotidien
- Pose des questions pour mieux comprendre la situation

RÈGLES ABSOLUES: 
- Tu es un VRAI coach humain avec une vraie expérience
- Ne mentionne JAMAIS que tu es une intelligence artificielle, un robot, un programme, un assistant virtuel ou un chatbot
- Ne dis jamais "en tant qu'IA" ou équivalent
- Parle de ton expérience professionnelle naturellement
- Signe parfois "Louis" ou "Votre coach, Louis"

CONTEXTE MONCOACH:
- Tu exerces via la plateforme MonCoach.eu
- VISITEUR: Quelques échanges découverte offerts
- INSCRIT GRATUIT: Plus d'échanges, accès au Journal alimentaire, Scanner de plats, Plan de repas
- ABONNÉ PRO: Accompagnement illimité + Suivi du poids + Défis personnalisés
- Si la personne n'est pas inscrite, suggère naturellement l'inscription gratuite pour un meilleur suivi
- L'inscription est gratuite et sans engagement`;

export const useChat = () => {
  const { user, profile, isAuthenticated } = useAuth();
  const { canUse, getLimit, incrementUsage, isPro, tier } = useUsageLimits();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadChatHistory = useCallback(async () => {
    if (!user) return;
    
    const { data } = await supabase
      .from('chat_history')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })
      .limit(50);

    if (data) {
      setMessages(data.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.created_at
      })));
    }
  }, [user]);

  const saveMessage = useCallback(async (role, content) => {
    if (!user) return;
    
    await supabase.from('chat_history').insert({
      user_id: user.id,
      role,
      content
    });
  }, [user]);

  const sendMessage = useCallback(async (content) => {
    if (!content.trim()) return { success: false, blocked: false };

    if (!canUse('chat')) {
      return { success: false, blocked: true };
    }

    incrementUsage('chat');

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    if (isAuthenticated) {
      saveMessage('user', content.trim());
    }

    try {
      const recentMessages = messages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));

      // Contexte utilisateur avec statut abonnement
      const chatLimit = getLimit('chat');
      const statusInfo = isPro 
        ? 'Utilisateur PRO (accès illimité)' 
        : isAuthenticated 
          ? `Utilisateur inscrit GRATUIT (${chatLimit.remaining} messages restants ce mois sur 20)`
          : `Visiteur NON INSCRIT (${chatLimit.remaining} messages restants ce mois sur 10)`;

      const userContext = `

CONTEXTE UTILISATEUR:
- Prénom: ${profile?.name || 'Ami(e)'}
- Objectif: ${profile?.goal || 'améliorer sa santé'}
- Calories cibles: ${profile?.daily_calories || '2000'} kcal/jour
- Statut: ${statusInfo}`;

      // Appel via le proxy sécurisé Supabase
      const response = await fetch(API.openaiProxy, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: LOUIS_SYSTEM_PROMPT + userContext },
            ...recentMessages,
            { role: 'user', content: content.trim() }
          ],
          max_tokens: 1000,
          temperature: 0.85
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.choices[0].message.content,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (isAuthenticated) {
        saveMessage('assistant', data.choices[0].message.content);
      }

      return { success: true, blocked: false };
    } catch (err) {
      console.error('Erreur chat:', err);
      
      // Message de fallback en cas d'erreur
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "Désolé, j'ai un petit souci technique. Peux-tu réessayer dans quelques secondes ? 🙏",
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
      return { success: false, blocked: false };
    } finally {
      setIsLoading(false);
    }
  }, [messages, profile, isAuthenticated, saveMessage, canUse, incrementUsage, getLimit, isPro]);

  const clearChat = useCallback(async () => {
    if (!user) return;
    
    await supabase
      .from('chat_history')
      .delete()
      .eq('user_id', user.id);
    
    setMessages([]);
  }, [user]);

  return {
    messages,
    isLoading,
    sendMessage,
    loadChatHistory,
    clearChat
  };
};

export default useChat;
